import React, { useMemo, useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_LOCAL_URL } from '../../../config';

import ProductOverviewForm from "../ProductManagement/ProductDataSheets/ProductOverviewForm";
import IntendedUseForm from "../ProductManagement/ProductDataSheets/IntendedUseForm";
import KeyFeaturesForm from "../ProductManagement/ProductDataSheets/KeyFeaturesForm";
import ProductSpecificationForm from "../ProductManagement/ProductDataSheets/ProductSpecificationForm";
import IngredientSpecificationForm from "../ProductManagement/ProductDataSheets/IngredientSpecificationForm";
import ProcessSpecificationForm from "../ProductManagement/ProductDataSheets/ProcessSpecificationForm";
import StabilityInformationForm from "../ProductManagement/ProductDataSheets/StabilityInformationForm";
import ScientificClinicalInformationForm from "../ProductManagement/ProductDataSheets/ScientificClinicalInformationForm";

const ENDPOINT_URL = `${API_BASE_LOCAL_URL}/api/products`;

export default function ProductRegisterForm({ initialData = null, onSubmit, onCancel }) {
  const hasLoadedProduct = useRef(false);
  const { productCode } = useParams();
  const isEdit = !!productCode;
  const navigate = useNavigate();
  const steps = [
    "Product Overview",
    "Intended Use",
    "Key Features",
    "Product Specification",
    "Ingredient Specification",
    "Process Specification",
    "Stability Information",
    "Scientific & Clinical",
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const [status, setStatus] = useState(null);
  const [globalErrors, setGlobalErrors] = useState([]);

  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => setStatus(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const emptyState = useMemo(
    () => ({
      productCode: "",
      productName: "",
      ingredientSources: "",
      productCategory: "",
      scope: "",
      origin: "",
      form: "",
      bannerImages: [],
      actives: [],
      productCompliance: [],
      productDescription: "",
      ingredientComposition: [],
      processDescription: "",
      productProcessingAids: [],
      uniqueProposition: "",
      stability: false,
      toxicity: false,
      activity: false,
      clinical: false,
      benefits: "",
      recommendedUse: "",
      directionsForUse: "",
      intendedUse: "",
      type: [],
      function: "",
      functionalUse: "",
      recommendation: "",
      disclaimer: "",
      botanicalIdentity: false,
      broadSpectrum: false,
      greenChemistry: false,
      agriculture: false,
      organicStewardship: false,
      qualityByDesign: false,
      thirdPartyEnabled: false,
      nonGmoValidation: false,
      climateSmartInterventions: false,
      livelihoodMission: false,
      igar: false,
      qcafMonitored: false,
      comprehensiveSafetyProfile: false,
      bioAvailabilityDifferential: false,
      realTimeStability: false,
      activeCellularNutrition: false,
      extractionType: "",
      ratio: "",
      drying: "",
      excipients: "",
      organolepticProperties: [],
      heavyMetals: [],
      microbiology: [],
      nutritionalInformation: [],
      allergenInformation: [],
      ingredients: [],
      processes: [],
    }),
    []
  );

  const [formData, setFormData] = useState(initialData || emptyState);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!productCode || initialData || hasLoadedProduct.current) return;

    hasLoadedProduct.current = true;
    const token = localStorage.getItem("token");

    axios
      .get(`${ENDPOINT_URL}/${productCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const safeData = {
          ...emptyState,
          ...res.data,
          bannerImages: Array.isArray(res.data.bannerImages) ? res.data.bannerImages : [],
          ingredients: (res.data.ingredients || []).map((ing) => ({
            ...ing,
            processingAids: ing.processingAids?.map((aid) => ({ ...aid })) || [],
          })),
          processes: (res.data.processes || []).map((proc) => ({
          ...proc,

          // REAL S3 KEYS
          processFlowChartUrl:
            proc.processFlowChartUrl || "",

          studyUrl:
            proc.studyUrl || "",

          msdsUrl:
            proc.msdsUrl || "",

          // SIGNED URLS FOR PREVIEW
          processFlowChartPreview:
            proc.processFlowChartSignedUrl || "",

          studyPreview:
            proc.studySignedUrl || "",

          msdsPreview:
            proc.msdsSignedUrl || "",
        })),
        };
        setFormData(safeData);
      })
      .catch((err) => console.error("Failed to fetch product data:", err));
  }, [productCode, initialData, emptyState]);

  const updateField = (field, value, meta) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => {
      const next = { ...prev };
      if (field === "productCode" && next.productCode) delete next.productCode;
      if (field === "productName" && next.productName) delete next.productName;
      return next;
    });
  };

  const validateStep = (stepIndex) => {
    const errors = {};
    const global = [];

    if (stepIndex === 0) {
      if (!formData.productCode?.trim()) {
        errors.productCode = "Product Code is required";
        global.push("Product Code is required");
      }
      if (!formData.productName?.trim()) {
        errors.productName = "Product Name is required";
        global.push("Product Name is required");
      }
    }

    if (stepIndex === 4) {
      const items = formData.ingredients || [];
      const stepErrors = {};
      if (!items.length) {
        stepErrors[0] = { ingredientCode: "Required", commonName: "Required", scientificName: "Required" };
        global.push("At least one Ingredient is required");
      } else {
        items.forEach((it, i) => {
          const e = {};
          if (!it?.ingredientCode?.trim()) e.ingredientCode = "Required";
          if (!it?.commonName?.trim()) e.commonName = "Required";
          if (!it?.scientificName?.trim()) e.scientificName = "Required";
          if (Object.keys(e).length) stepErrors[i] = e;
        });
      }
      if (Object.keys(stepErrors).length) errors.ingredients = stepErrors;
    }

    if (stepIndex === 5) {
      const items = formData.processes || [];
      const stepErrors = {};
      if (!items.length) {
        stepErrors[0] = { processCode: "Required", processDescription: "Required" };
        global.push("At least one Process is required");
      } else {
        items.forEach((it, i) => {
          const e = {};
          if (!it?.processCode?.trim()) e.processCode = "Required";
          if (!it?.processDescription?.trim()) e.processDescription = "Required";
          if (Object.keys(e).length) stepErrors[i] = e;
        });
      }
      if (Object.keys(stepErrors).length) errors.processes = stepErrors;
    }

    setFormErrors(errors);
    setGlobalErrors(global);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => validateStep(currentStep) && currentStep < steps.length - 1 && setCurrentStep((s) => s + 1);
  const prevStep = () => currentStep > 0 && setCurrentStep((s) => s - 1);
  const goToStep = (index) => setCurrentStep(index);

  const handleFinalSubmit = async () => {
    try {
      if (!validateStep(0) || !validateStep(4) || !validateStep(5)) return;

      // Deep copy data to avoid mutating UI state
      const rawData = { ...formData};
      const isEditing = !!productCode;
      const method = isEditing ? "PUT" : "POST";
      const url = isEditing ? `${ENDPOINT_URL}/${formData.productCode}` : ENDPOINT_URL;

      const fd = new FormData();
      
      const productJson = {
  ...rawData,

  processes: (rawData.processes || []).map((proc) => ({

    processCode: proc.processCode,
    processDescription: proc.processDescription,
    criticalSteps: proc.criticalSteps,

    physical: proc.physical,
    chemical: proc.chemical,
    biological: proc.biological,
    killStepReview: proc.killStepReview,

    rawMaterial: proc.rawMaterial,
    processControls: proc.processControls,
    organicControls: proc.organicControls,
    qualityControl: proc.qualityControl,
    thirdPartySampling: proc.thirdPartySampling,

    processFlowChartUrl: proc.processFlowChartUrl,
    studyUrl: proc.studyUrl,
    msdsUrl: proc.msdsUrl,

    acceleratedStability: proc.acceleratedStability,
    realTimeStability: proc.realTimeStability,
    oxidativeStability: proc.oxidativeStability,
    photoStability: proc.photoStability,
    radiationStudy: proc.radiationStudy,

    shelfLife: proc.shelfLife,
    handling: proc.handling,
    capsule: proc.capsule,
    tablet: proc.tablet,
    foodPreparations: proc.foodPreparations,
    cosmeticPreparations: proc.cosmeticPreparations,
    mixability: proc.mixability,

    studyType: proc.studyType,
    significance: proc.significance,
    validation: proc.validation,
    references: proc.references,

  })),
};

      // Remove non-JSON fields
      delete productJson.bannerImages;

      fd.append("product", new Blob([JSON.stringify(productJson)], { type: "application/json" }));

      // Attach banner image files
      if (Array.isArray(formData.bannerImages)) {
        formData.bannerImages.forEach((file) => {
          if (file instanceof File) {
            fd.append("bannerImage", file);
          }
        });
      }
    // =====================================
    // ATTACH PROCESS FILES
    // =====================================
(formData.processes || []).forEach((proc) => {

  if (proc.processFlowChartFile instanceof File) {
    fd.append(
      "processFlowChartFiles",
      proc.processFlowChartFile
    );
  }

  if (proc.studyFile instanceof File) {
    fd.append(
      "studyFiles",
      proc.studyFile
    );
  }

  if (proc.msdsFile instanceof File) {
    fd.append(
      "msdsFiles",
      proc.msdsFile
    );
  }

});
      const token = localStorage.getItem("token");
      await axios({
        method,
        url,
        data: fd,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setStatus({ type: "success", message: `✅ Product ${isEditing ? "updated" : "added"} successfully!` });
      if (onSubmit) onSubmit();

      setTimeout(() => {
        if (onCancel) onCancel();
        else navigate("/AdminDashboard/products");
      }, 2500);

    } catch (err) {
      console.error("Submission error:", err);
      setStatus({ type: "error", message: err.response?.data?.message || "❌ Operation failed." });
    }
  };

  const commonProps = {
    data: formData,
    onChange: updateField,
    onNext: nextStep,
    onBack: prevStep,
    errors: formErrors,
    isEdit,
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0: return <ProductOverviewForm {...commonProps} onBack={undefined} />;
      case 1: return <IntendedUseForm {...commonProps} />;
      case 2: return <KeyFeaturesForm {...commonProps} />;
      case 3: return <ProductSpecificationForm {...commonProps} />;
      case 4: return <IngredientSpecificationForm {...commonProps} errors={formErrors.ingredients || {}} />;
      case 5: return <ProcessSpecificationForm {...commonProps} errors={formErrors.processes || {}} />;
      case 6: return <StabilityInformationForm {...commonProps} />;
      case 7: return <ScientificClinicalInformationForm {...commonProps} onNext={undefined} onSubmit={handleFinalSubmit} />;
      default: return null;
    }
  };

  return (
    <div className="container mt-4 mb-5">
      <div className="container-fluid px-0">
        <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
          <div>
            <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '1.15rem' }}>
              {isEdit ? "Edit Product" : "Add New Product"}
            </h5>
            <p className="text-muted mb-0" style={{ fontSize: '0.7rem' }}>
              Section {currentStep + 1} of {steps.length}: {steps[currentStep]}
            </p>
          </div>
          <button 
            className="btn btn-light btn-sm border rounded-circle shadow-sm" 
            style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => onCancel ? onCancel() : window.history.back()}
          >
            <i className="bi bi-x-lg" style={{ fontSize: '12px' }}></i>
          </button>
        </div>

        {/* STEPPER UI */}
        <div className="position-relative mb-5 px-2">
          <div className="position-absolute start-0 w-100" style={{ height: '2px', background: '#e9ecef', top: '14px', zIndex: 0 }}></div>
          <div className="d-flex justify-content-between align-items-start position-relative" style={{ zIndex: 1 }}>
            {steps.map((label, index) => (
              <div key={label} className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
                <button
                  type="button"
                  onClick={() => goToStep(index)}
                  className={`btn d-flex align-items-center justify-content-center border-2 p-0 mb-2 ${index === currentStep ? 'shadow' : ''}`}
                  style={{ 
                    width: 28, height: 28, fontSize: 11, fontWeight: 'bold', borderRadius: '50%',
                    backgroundColor: index === currentStep ? '#0d6efd' : index < currentStep ? '#198754' : '#fff',
                    borderColor: index === currentStep ? '#0d6efd' : index < currentStep ? '#198754' : '#dee2e6',
                    color: (index <= currentStep) ? '#fff' : '#6c757d',
                    zIndex: 2
                  }}
                >
                  {index < currentStep ? "✓" : index + 1}
                </button>
                <small className={`text-center d-none d-lg-block ${index === currentStep ? 'text-primary fw-bold' : 'text-muted'}`} style={{ fontSize: '0.65rem' }}>
                  {label}
                </small>
              </div>
            ))}
          </div>
        </div>

        {/* PROGRESS BAR */}
        <div className="progress mx-auto mb-4" style={{ height: 4, width: '98%', borderRadius: 10 }}>
          <div className="progress-bar bg-success" style={{ width: `${((currentStep + 1) / steps.length) * 100}%`, transition: 'width 0.5s ease' }} />
        </div>
      </div>

      <div className="card shadow-sm p-4">{renderStep()}</div>

      {globalErrors.length > 0 && (
        <div className="alert alert-danger mt-3">
          <ul className="mb-0">{globalErrors.map((err, idx) => <li key={idx}>{err}</li>)}</ul>
        </div>
      )}

      {status && (
        <div className={`alert alert-${status.type === "success" ? "success" : "danger"} alert-dismissible fade show mt-3`}>
          {status.message}
          <button type="button" className="btn-close" onClick={() => setStatus(null)} />
        </div>
      )}
    </div>
  );
}